import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { subscriptionsApi } from '../api/subscriptions';
import { queryKeys } from '../lib/queryKeys';
import type { PaginationParams } from '../types/api';
import type { AdminUpgradeRequest, AdminDowngradeRequest, AdminCancelRequest, AdminRefundRequest, AdminExtendTrialRequest, AdminOverrideUsageRequest, AdminStatusChangeRequest } from '../types/subscriptions';

export function useSubscriptionsList(params: PaginationParams & { status?: string; plan?: string }) {
  return useQuery({ queryKey: queryKeys.subscriptions.list(params), queryFn: () => subscriptionsApi.list(params) });
}

export function useSubscriptionDetail(id: string) {
  return useQuery({ queryKey: queryKeys.subscriptions.detail(id), queryFn: () => subscriptionsApi.detail(id), enabled: !!id });
}

function useSubAction<T>(action: (id: string, data: T) => Promise<unknown>, msg: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: T }) => action(id, data),
    onSuccess: (_, { id }) => { qc.invalidateQueries({ queryKey: queryKeys.subscriptions.detail(id) }); qc.invalidateQueries({ queryKey: queryKeys.subscriptions.all }); qc.invalidateQueries({ queryKey: queryKeys.analytics.dashboard }); message.success(msg); },
  });
}

export const useUpgradeSubscription = () => useSubAction<AdminUpgradeRequest>(subscriptionsApi.upgrade, 'Subscription upgraded');
export const useDowngradeSubscription = () => useSubAction<AdminDowngradeRequest>(subscriptionsApi.downgrade, 'Subscription downgraded');
export const useCancelSubscription = () => useSubAction<AdminCancelRequest>(subscriptionsApi.cancel, 'Subscription canceled');
export const useRefundSubscription = () => useSubAction<AdminRefundRequest>(subscriptionsApi.refund, 'Refund processed');
export const useExtendTrial = () => useSubAction<AdminExtendTrialRequest>(subscriptionsApi.extendTrial, 'Trial extended');
export const useOverrideUsage = () => useSubAction<AdminOverrideUsageRequest>(subscriptionsApi.overrideUsage, 'Usage limit overridden');
export const useChangeSubscriptionStatus = () => useSubAction<AdminStatusChangeRequest>(subscriptionsApi.changeStatus, 'Status updated');
